import React, { useState, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormContext } from 'react-hook-form';
import { Typography, Button, Box, Grid } from '@material-ui/core';
import MuiLink from '@material-ui/core/Link';
import { Redirect, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import UnathorizedLayout from '../../shared/layout/UnauthorizedLayout';
import FormInput from '../../shared/form/FormInput';
import FormPasswordInput from '../../shared/form/FormPasswordInput';
import { AlertContext } from '../../shared/app/AlertProvider';
import { cognitoErrorType, IStatus } from '../../../types';
import { FormBox } from '../../shared/form-box';
import FormCheckbox from '../../shared/form/FormCheckbox';
import { useStyles } from './styles';
import i18nInstance from '../../../translations';
import { validatePassword } from '../../../utils/cognitoValidation';

const Registration = () => {
  const classes = useStyles();

  const termsURL = process.env.REACT_APP_AGB_URL;
  const privacyURL = process.env.REACT_APP_PRIVACY_URL;
  const contactEmail = process.env.REACT_APP_CONTACT_EMAIL;

  const { search } = useLocation();
  const emailInUrl = new URLSearchParams(search).get('email');

  const [submitStatus, setSubmitStatus] = useState<IStatus>({
    value: 'idle',
    code: null,
  });

  const [redirect, setRedirect] = useState(false);
  const [username, setUsername] = useState();
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showPasswordHint, setShowPasswordHint] = useState(true);

  const { showAlert } = useContext(AlertContext);

  const { t } = useTranslation();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    submitFocusError: false,
    defaultValues: {
      firstName: '',
      email: '',
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = useCallback(
    async (data) => {
      setSubmitStatus({ value: 'pending', code: null });
      try {
        await Auth.signUp({
          username: data.email, // we use the email also as username attribute
          password: data.password,
          attributes: {
            email: data.email,
            given_name: data.firstName,
            locale: i18nInstance.language,
            'custom:agreed_to_data': data['codeOfConductTOS-data'] ? '1' : '0',
            'custom:agreed_to_contact': data['codeOfConductTOS-disclamer']
              ? '1'
              : '0',
          },
        });
        showAlert({
          severity: 'success',
          message: t('v2.verification.snackbar_label_verificationEmailSentOut'),
        });
        setUsername(data.email);
        setRedirect(true);
      } catch (e: any) {
        switch (e.code) {
          case cognitoErrorType.USERNAME_EXISTS_EXCEPTION:
            setErrorMsg(
              t(
                'v2.global.textField_errorHandling_helperText_companyEmail_taken',
              ),
            );
            break;
          case cognitoErrorType.USER_LAMBDA_VALIDATION_EXCEPTION:
            if (e.message === 'PreSignUp failed with error Unknown Domain.') {
              setErrorMsg(
                t(
                  'v2.global.textField_errorHandling_helperText_companyEmail_invalid',
                ),
              );
            } else {
              showAlert({
                severity: 'error',
                message: t('v2.global.snackbar_label_error_somethingWentWrong'),
              });
            }
            break;
          default:
            showAlert({
              severity: 'error',
              message: t('v2.global.snackbar_label_error_somethingWentWrong'),
            });
        }
        setSubmitStatus({ value: 'idle', code: null });
      }
    },
    [showAlert, t],
  );

  useEffect(() => {
    if (emailInUrl) {
      setEmail(emailInUrl);
    }
  }, [emailInUrl]);

  if (redirect) {
    return (
      <Redirect
        to={{
          pathname: '/registration/success',
          state: { username },
        }}
      />
    );
  }

  return (
    <UnathorizedLayout
      title={t('v2.registration.header_label_title')}
      subtitle={t('v2.registration.header_label_subTitle')}
    >
      <FormContext {...methods}>
        <FormBox
          component="form"
          p={3}
          pt={2}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInput
                id="registration-firstname"
                name="firstName"
                label={t('v2.global.textField_label_userName')}
                placeholder={t('v2.global.textField_placeholder_userName')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                id="registration-email"
                type="email"
                name="email"
                label={t('v2.global.textField_label_companyEmail')}
                placeholder={t('v2.global.textField_placeholder_companyEmail')}
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                }}
                required
              />
              <p className={classes.errorMsg}> {errorMsg} </p>
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore next */}
              <FormPasswordInput
                id="login-password"
                name="password"
                variant="body1"
                label={t('v2.global.textField_label_password')}
                placeholder={t('v2.global.textField_placeholder_password')}
                validate={(value: string) => {
                  if (
                    validatePassword(value) &&
                    validatePassword(value).includes('errorHandling')
                  ) {
                    setShowPasswordHint(false);
                    return t(validatePassword(value));
                  }
                  setShowPasswordHint(true);
                }}
                required
              />
              {showPasswordHint && (
                <span>
                  {t(
                    'v2.global.textField_errorHandling_helperText_password_requirements_specialCharacter',
                  )}
                </span>
              )}
            </Grid>
            <Grid item xs={12}>
              {t('v2.registration.termsAndConditions_label_body_part1')}
              <MuiLink
                component="a"
                variant="body2"
                href={termsURL}
                target="_blank"
                className={classes.links}
              >
                {t(
                  'v2.registration.termsAndConditions_button_termsAndConditions',
                )}
              </MuiLink>
              {t('v2.registration.termsAndConditions_label_body_part2')}
              <MuiLink
                component="a"
                variant="body2"
                href={privacyURL}
                target="_blank"
                className={classes.links}
              >
                {t('v2.registration.termsAndConditions_button_privacyPolicy')}
              </MuiLink>
            </Grid>
            <Grid item xs={12}>
              <FormCheckbox
                id="code-of-conduct-data"
                name="codeOfConductTOS-data"
                required
              >
                * {t('v2.registration.termsAndConditions_checkbox_label_data')}
              </FormCheckbox>
            </Grid>
            <Grid item xs={12}>
              <FormCheckbox
                id="code-of-conduct-disclamer"
                name="codeOfConductTOS-disclamer"
              >
                {t(
                  'v2.registration.termsAndConditions_checkbox_label_emailInformation',
                )}
              </FormCheckbox>
            </Grid>

            <Grid item xs={12}>
              <Box pt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitStatus.value === 'pending'}
                >
                  {t('v2.registration.button_label_createAccount')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                {t('v2.global.label_userHasAnAccount')}
                <MuiLink component="a" href="/login" className={classes.links}>
                  {t('v2.global.button_userHasAnAccount')}
                </MuiLink>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                {t('v2.global.label_needHelp')}
                <MuiLink
                  component="a"
                  href={`mailto:${contactEmail}`}
                  className={classes.links}
                >
                  {t('v2.global.button_needHelp')}
                </MuiLink>
              </Typography>
            </Grid>
          </Grid>
        </FormBox>
      </FormContext>
    </UnathorizedLayout>
  );
};

export default Registration;
